import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { getProfile, updateProfile, uploadAvatar, changePassword, sendNotificationEmail, updateUser } from "../../lib/api";
import { Camera, Save, Lock, Check, Trash2, Loader2, User, Mail, Shield, Eye, EyeOff } from "lucide-react";

export default function AdminProfile() {
  const { user, refreshProfile, updateUserState } = useAuth();
  const [profile, setProfile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [accountName, setAccountName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [changingPwd, setChangingPwd] = useState(false);
  const [pwdDone, setPwdDone] = useState(false);
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  useEffect(() => {
    if (!user) return;
    console.log("[Profile] User ID:", user.id);
    setEmail(user.email || "");
    setAccountName(user.name || "");

    getProfile(user.id).then((p) => {
      console.log("[Profile] Profile result:", p);
      if (p) {
        setProfile(p);
        setDisplayName(p.display_name || "");
        setAvatarUrl(p.avatar_url || "");
      }
    }).catch((err) => {
      console.error("Error cargando perfil:", err);
    });
  }, [user]);

  async function handleSave() {
    setSaving(true);
    try {
      const updatedUser = await updateUser(user.id, { email: email.trim(), name: accountName.trim() });
      updateUserState(updatedUser);
      const updatedProfile = await updateProfile(user.id, { display_name: displayName.trim() });
      setProfile(updatedProfile);
      await refreshProfile();
      toast.success("Perfil e conta atualizados.");
    } catch (err) {
      console.error("Failed to save profile/user:", err);
      toast.error("Falha ao salvar as alterações.");
    } finally {
      setSaving(false);
    }
  }

  async function handleAvatar(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadAvatar(file, user.id);
      const updated = await updateProfile(user.id, { avatar_url: url });
      setAvatarUrl(url);
      setProfile(updated);
      await refreshProfile();
      toast.success("Foto atualizada.");
    } catch (err) {
      console.error("Failed to upload avatar:", err);
      toast.error("Falha ao enviar foto.");
    } finally {
      setUploading(false);
    }
  }

  async function handleRemoveAvatar() {
    if (!avatarUrl) return;
    try {
      const updated = await updateProfile(user.id, { avatar_url: "" });
      setAvatarUrl("");
      setProfile(updated);
      await refreshProfile();
      toast.success("Foto removida.");
    } catch (err) {
      console.error("Failed to remove avatar:", err);
      toast.error("Falha ao remover foto.");
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (pwdNew !== pwdConfirm) return toast.error("Senhas não conferem.");
    if (pwdNew.length < 6) return toast.error("Mínimo 6 caracteres.");
    setChangingPwd(true);
    setPwdDone(false);
    try {
      await changePassword(user.id, pwdCurrent, pwdNew);
      await sendNotificationEmail(
        user.email,
        "Hub Profissional — Senha alterada",
        `<p>Sua senha foi alterada com sucesso.</p><p>Se você não fez esta alteração, responda este email imediatamente.</p>`
      );
      setPwdDone(true);
      setPwdCurrent(""); setPwdNew(""); setPwdConfirm("");
      toast.success("Senha alterada. Email de confirmação enviado.");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Falha ao alterar senha.");
    } finally {
      setChangingPwd(false);
    }
  }

  return (
    <div data-testid="admin-profile-tab" className="max-w-5xl mx-auto space-y-8">
      {!profile ? (
        <div className="bg-white border border-fendi rounded-xl p-8 text-center">
          <Shield size={32} className="mx-auto text-warmgray/50 mb-4" strokeWidth={1} />
          <h2 className="font-serif text-2xl text-softblack mb-2">Perfil não configurado</h2>
          <p className="text-warmgray text-sm mb-6">Entre em contato com o suporte para ativar seu perfil.</p>
        </div>
      ) : (
        <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-4xl text-softblack">Meu Perfil</h2>
          <p className="text-warmgray text-sm mt-1">Gerencie suas informações e preferências</p>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left Sidebar - Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-fendi rounded-xl p-6 sticky top-24 shadow-sm">
            {/* Avatar */}
            <div className="text-center mb-6">
              <div className="relative w-28 h-28 mx-auto rounded-full overflow-hidden bg-gradient-to-br from-champagne/20 to-fendi/30 border-2 border-fendi/40 group">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User size={40} className="text-warmgray/40" strokeWidth={1} />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/50 backdrop-blur-sm cursor-pointer flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 text-white transition-all duration-300 rounded-full z-10">
                  {uploading ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>
                      {avatarUrl && (
                        <button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRemoveAvatar(); }}
                          className="mb-1 hover:text-red-400 transition-colors"
                          title="Remover Foto"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                      <label className="cursor-pointer flex items-center justify-center">
                        <Camera size={18} />
                        <input type="file" accept="image/*" className="hidden" onChange={handleAvatar} disabled={uploading} />
                      </label>
                    </>
                  )}
                </div>
              </div>
              <h3 className="font-serif text-xl text-softblack mt-4">{displayName || "Sem nome"}</h3>
              <p className="text-warmgray text-xs mt-1">{user?.email}</p>
            </div>

            {/* Quick Info */}
            <div className="space-y-3 pt-4 border-t border-fendi/50">
              <div className="flex items-center gap-2 text-sm text-warmgray">
                <User size={14} strokeWidth={1.5} className="text-champagne" />
                <span className="truncate">{accountName || "—"}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-warmgray">
                <Mail size={14} strokeWidth={1.5} className="text-champagne" />
                <span className="truncate">{email}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Info Card */}
          <div className="bg-white border border-fendi rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-champagne/10 flex items-center justify-center">
                <User size={18} className="text-champagne" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-softblack">Informações Pessoais</h3>
                <p className="text-xs text-warmgray">Seus dados de identificação</p>
              </div>
            </div>

            <div className="space-y-5">
              <label className="block">
                <span className="eyebrow text-xs">Nome da conta</span>
                <div className="relative mt-2">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" strokeWidth={1.5} />
                  <input
                    className="editorial-field pl-9"
                    value={accountName}
                    onChange={(e) => setAccountName(e.target.value)}
                    placeholder="Nome real ou admin"
                  />
                </div>
              </label>

              <label className="block">
                <span className="eyebrow text-xs">E-mail</span>
                <div className="relative mt-2">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" strokeWidth={1.5} />
                  <input
                    type="email"
                    className="editorial-field pl-9"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                  />
                </div>
              </label>

              <label className="block">
                <span className="eyebrow text-xs">Nome de exibição</span>
                <div className="relative mt-2">
                  <Shield size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" strokeWidth={1.5} />
                  <input
                    data-testid="profile-input-name"
                    className="editorial-field pl-9"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="Seu nome público"
                  />
                </div>
              </label>
            </div>

            <button onClick={handleSave} disabled={saving} className="btn-primary w-full mt-6" data-testid="profile-save">
              {saving ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : (
                <Save size={14} strokeWidth={1.5} className="mr-2" />
              )}
              {saving ? "Salvando..." : "Salvar Alterações"}
            </button>
          </div>

          {/* Security Card */}
          <div className="bg-white border border-fendi rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-champagne/10 flex items-center justify-center">
                <Lock size={18} className="text-champagne" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif text-xl text-softblack">Segurança</h3>
                <p className="text-xs text-warmgray">Alterar senha de acesso</p>
              </div>
            </div>

            {pwdDone ? (
              <div className="flex items-start gap-3 bg-green-50/50 border border-green-200/60 rounded-lg p-4">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={16} className="text-green-600" strokeWidth={2} />
                </div>
                <div>
                  <p className="font-medium text-sm text-softblack">Senha alterada com sucesso</p>
                  <p className="text-xs text-warmgray mt-0.5">Email de confirmação enviado para {user?.email}</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleChangePassword} className="space-y-5">
                <label className="block">
                  <span className="eyebrow text-xs">Senha atual</span>
                  <div className="relative mt-2">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" strokeWidth={1.5} />
                    <input
                      type={showCurrentPwd ? "text" : "password"}
                      data-testid="pwd-current"
                      className="editorial-field pl-9 pr-10"
                      value={pwdCurrent}
                      onChange={(e) => setPwdCurrent(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPwd(!showCurrentPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-warmgray/60 hover:text-softblack transition-colors"
                    >
                      {showCurrentPwd ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                    </button>
                  </div>
                </label>

                <label className="block">
                  <span className="eyebrow text-xs">Nova senha</span>
                  <div className="relative mt-2">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" strokeWidth={1.5} />
                    <input
                      type={showNewPwd ? "text" : "password"}
                      data-testid="pwd-new"
                      className="editorial-field pl-9 pr-10"
                      value={pwdNew}
                      onChange={(e) => setPwdNew(e.target.value)}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPwd(!showNewPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-warmgray/60 hover:text-softblack transition-colors"
                    >
                      {showNewPwd ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                    </button>
                  </div>
                </label>

                <label className="block">
                  <span className="eyebrow text-xs">Confirmar nova senha</span>
                  <div className="relative mt-2">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-warmgray/60" strokeWidth={1.5} />
                    <input
                      type={showConfirmPwd ? "text" : "password"}
                      data-testid="pwd-confirm"
                      className="editorial-field pl-9 pr-10"
                      value={pwdConfirm}
                      onChange={(e) => setPwdConfirm(e.target.value)}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-warmgray/60 hover:text-softblack transition-colors"
                    >
                      {showConfirmPwd ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                    </button>
                  </div>
                </label>

                <button type="submit" disabled={changingPwd} className="btn-primary w-full" data-testid="pwd-submit">
                  {changingPwd ? (
                    <Loader2 className="animate-spin mr-2" size={16} />
                  ) : (
                    <Lock size={14} strokeWidth={1.5} className="mr-2" />
                  )}
                  {changingPwd ? "Alterando..." : "Alterar Senha"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
}
